import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listConversations } from "@/lib/api/chat";
import type { ConversationListItem } from "@/lib/api/chat";

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  activeConversationId: string | null;
  onPick: (id: string | null) => void;
}

export function ConversationSidebar({
  open,
  onToggle,
  activeConversationId,
  onPick,
}: SidebarProps) {
  const { t } = useTranslation();
  const query = useQuery<ConversationListItem[], Error>({
    queryKey: ["chat", "conversations"],
    queryFn: () => listConversations(),
    refetchInterval: 30_000,
  });

  return (
    <aside
      className={`flex flex-col bg-pantera-900 transition-all duration-200 ${
        open ? "w-64" : "w-14"
      }`}
      style={{ borderRight: "1px solid rgba(255,255,255,0.04)" }}
    >
      {/* New conversation button */}
      <div className={`flex-shrink-0 ${open ? "p-4 pb-2" : "p-2"}`}>
        {open ? (
          <button
            type="button"
            onClick={() => onPick(null)}
            className="flex w-full items-center justify-center gap-2 rounded-pill bg-accent-500 px-4 py-2.5 text-sm font-bold text-pantera-900 shadow-cta hover:bg-accent-600 transition-colors"
          >
            <PlusIcon />
            {t("chat.history.new")}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              onPick(null);
              onToggle();
            }}
            className="flex h-10 w-10 items-center justify-center rounded-pill bg-accent-500 text-pantera-900 hover:bg-accent-600 transition-colors"
            title={t("chat.history.new")}
          >
            <PlusIcon />
          </button>
        )}
      </div>

      {/* Toggle button */}
      <div
        className={`flex flex-shrink-0 ${open ? "px-4 pb-1 justify-end" : "px-2 justify-center"}`}
      >
        <button
          type="button"
          onClick={onToggle}
          className="rounded-lg p-1.5 text-white/40 hover:bg-white/5 hover:text-white/70 transition-colors"
          title={open ? t("chat.history.collapse") : t("chat.history.expand")}
        >
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </button>
      </div>

      {/* Conversations list */}
      {open && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="px-4 pb-2 pt-3">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">
              {t("chat.history.title")}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto px-2 pb-4">
            {query.isLoading && (
              <p className="px-2 py-2 text-xs text-white/40">{t("chat.history.loading")}</p>
            )}
            {query.isError && (
              <p className="px-2 py-2 text-xs text-error/80">
                {t("chat.history.error", { message: query.error?.message ?? "" })}
              </p>
            )}
            {query.data && query.data.length === 0 && (
              <p className="px-2 py-2 text-xs text-white/40">{t("chat.history.empty")}</p>
            )}
            <ul className="space-y-0.5">
              {(query.data ?? []).map((c) => {
                const isActive = c.id === activeConversationId;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => onPick(c.id)}
                      className={`relative w-full rounded-lg px-3 py-2.5 text-left text-[13px] transition-colors ${
                        isActive
                          ? "bg-pantera-500/20 text-white"
                          : "text-white/65 hover:bg-white/5 hover:text-white/85"
                      }`}
                    >
                      {isActive && (
                        <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-accent-500" />
                      )}
                      <div className={`line-clamp-2 ${isActive ? "font-semibold" : "font-medium"}`}>
                        {c.title}
                      </div>
                      <div className="mt-1 flex justify-between text-[11px] text-white/35">
                        <span>{formatStartDate(c.created_at)}</span>
                        <span>{c.message_count} msg</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Powered by Pantera AI footer */}
      {open && (
        <div
          className="flex flex-shrink-0 items-center gap-2 px-4 py-3 text-[11px] text-white/45"
          style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
        >
          <PanteraMark />
          <span className="font-medium">Powered by</span>
          <span className="font-bold text-white/65">Pantera AI</span>
        </div>
      )}
    </aside>
  );
}

function formatStartDate(iso: string): string {
  const d = new Date(iso);
  const datePart = d.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
  const timePart = d.toLocaleTimeString("es-MX", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${datePart} · ${timePart}`;
}

function PlusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

function PanteraMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path
        d="M4 20c0-6 3.5-11 8-11s8 5 8 11"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="9" cy="13" r="1.2" fill="currentColor" />
      <circle cx="15" cy="13" r="1.2" fill="currentColor" />
    </svg>
  );
}
